import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import filterOptions from "react-select/lib/utils/defaultFilterOptions";

import { setMessage } from "actions/message";
import { getGroupsAsync } from "settings/actions/users";
import TextInput from "components/library/TextInput";
import Button from "components/library/Button";
import UserImage from "settings/components/UserImage";
import LinkButton from "components/library/LinkButton";
import Spinner from "components/library/Spinner";
import WithPermissions from "components/library/WithPermissions";
import Label from "components/library/Label";
import AsyncDropdown from "components/library/AsyncDropdown";
import DisableUserConfirmation from "settings/components/DisableUserConfirmation";

import styles from "./UserForm.css";

class UserForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errors: {},
      user: props.user,
      confirmed: false,
      mustConfirm: false,
    };

    this.validate = this._validate.bind(this);
    this.save = this._save.bind(this);
    this.update = this._update.bind(this);
    this.updateGroup = this._updateGroup.bind(this);
    this.currentGroup = this._currentGroup.bind(this);
    this.confirm = this._confirm.bind(this);
    this.cancelConfirm = this._cancelConfirm.bind(this);
    this.confirmationGate = this._confirmationGate.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.user && nextProps.user !== this.state.user) {
      this.setState({ user: nextProps.user });
    }
  }

  _validate() {
    const errors = {};

    for (let field of this.props.fields) {
      if (field.required && !this.state.user[field.slug]) {
        errors[field.slug] = `${field.name} is required`;
      }
    }

    if (this.props.showPermissions && !this.state.user.groups) {
      errors.groups = "Permissions level is required.";
    }

    this.setState({ errors });

    if (Object.keys(errors).length) {
      return Promise.reject(errors);
    }
    return Promise.resolve();
  }

  _confirmationGate(action) {
    const { user, mustConfirm, confirmed } = this.state;

    if (!confirmed && user.groups && user.groups.length === 0) {
      this.setState({ mustConfirm: true });
    } else if (!mustConfirm || (mustConfirm && confirmed)) {
      action();
    }
  }

  _save() {
    const { user } = this.state;

    return this.validate()
      .then(() => this.props.save(user))
      .then(() => {
        if (user.groups.length === 0) {
          this.props.setMessage({
            message: `✓ ${user.display_name} was disabled`,
            type: "info",
          });
        } else {
          this.props.setMessage({
            message: `✓ ${this.props.successText}`,
            type: "success",
          });
        }
      })
      .catch(error => {
        let checkFieldErrors = { ...error };
        if ("context" in error) {
          checkFieldErrors = error.context.decodedJSON;
        }

        if (
          Object.keys(checkFieldErrors).length &&
          Object.keys(checkFieldErrors).every(field =>
            ["groups", "photo", ...this.props.fields.map(f => f.slug)].includes(
              field
            )
          )
        ) {
          this.setState({ errors: checkFieldErrors });
          this.props.setMessage({
            message: new Error(
              "One or more fields were invalid, please fix them below before continuing."
            ),
          });
        } else {
          this.props.setMessage({ message: error });
        }
      });
  }

  _update(e) {
    this.setState({
      user: { ...this.state.user, [e.target.name]: e.target.value },
    });
  }

  _updateGroup(selected) {
    const { user } = this.state;
    const newGroups = [];

    if (selected.name !== "disabled") {
      newGroups.push(selected);

      // @TODO: Even though we don't display the authors group, you shouldn't be able to take
      // people out of it - this make sure if they're in it, they stay in it
      const authorsGroup =
        user.groups &&
        user.groups.length &&
        user.groups.find(option => option.name === "authors");
      if (authorsGroup) {
        newGroups.push(authorsGroup);
      }
    }

    this.update({
      target: {
        name: "groups",
        value: newGroups,
      },
    });
  }

  _currentGroup() {
    const {
      user: { groups },
    } = this.state;

    if (groups && groups.length) {
      // Show the group with the highest ID (the most privileged one)
      return groups.sort((groupA, groupB) => groupA.id - groupB.id)[0];
    } else if (this.props.allowDisable) {
      return { name: "disabled" };
    } else {
      return null;
    }
  }

  _confirm() {
    this.setState({ confirmed: true }, () => this.confirmationGate(this.save));
  }

  _cancelConfirm() {
    this.setState({ mustConfirm: false });
  }

  render() {
    const { saveImage } = this.props;
    const { user, errors, mustConfirm, confirmed } = this.state;

    if (!user || user.loading) {
      return (
        <div className={styles.emptyState}>
          <Spinner />
        </div>
      );
    }

    if (mustConfirm && !confirmed) {
      return (
        <DisableUserConfirmation
          user={user}
          onClick={this.confirm}
          wasClicked={confirmed}
          onCancel={this.cancelConfirm}
        />
      );
    }

    return (
      <div className={styles.fieldset}>
        <h1 className={styles.header}>{this.props.titleText}</h1>
        {this.props.fields.map(field => (
          <TextInput
            key={field.slug}
            name={field.slug}
            className={styles.userField}
            type="text"
            label={field.name}
            placeholder={`Add a ${field.name.toLowerCase()}`}
            value={user[field.slug]}
            error={errors[field.slug]}
            required={field.required}
            onChange={this.update}
          />
        ))}
        {this.props.showPermissions && (
          <WithPermissions required={["users.edit_group"]}>
            <Label htmlFor="group">Permissions level</Label>
            <AsyncDropdown
              inputId="groups"
              labelKey="name"
              valueKey="id"
              className={classNames(styles.userField, styles.groups)}
              placeholder="Select a permissions level"
              value={this.currentGroup()}
              onChange={this.updateGroup}
              loadOptions={() =>
                this.props.getGroupsAsync().then(options => ({
                  options: this.props.allowDisable
                    ? [...options, { name: "disabled" }]
                    : options,
                }))
              }
              // @TODO: The authors group will be removed soon, so this filter can also be removed
              // the "authors" group is not a real permissions level and we shouldn't set people to it
              filterOptions={(...props) =>
                filterOptions(...props).filter(
                  option => option.name !== "authors"
                )
              }
              error={errors.groups}
            />
          </WithPermissions>
        )}
        {user.username && this.props.showPhoto && (
          <UserImage
            image={user.photo}
            username={user.username}
            removeUserImage={() => saveImage(user.username, null)}
          />
        )}
        <Button
          type="submit"
          className={styles.update}
          onClick={() => this.confirmationGate(this.save)}
        >
          {this.props.submitText}
        </Button>
        {this.props.showCancel && (
          <LinkButton type="cancel" to={"/settings/users"}>
            Cancel
          </LinkButton>
        )}
      </div>
    );
  }
}

export default connect(
  () => ({}),
  {
    setMessage,
    getGroupsAsync,
  }
)(UserForm);

UserForm.defaultProps = {
  showCancel: false,
  showPermissions: false,
  showPhoto: true,
  submitText: "Update",
  successText: "Account updated",
  allowDisable: true,
};

UserForm.propTypes = {
  titleText: PropTypes.string,
  submitText: PropTypes.string,
  successText: PropTypes.string,
  showCancel: PropTypes.bool,
  showPermissions: PropTypes.bool,
  showPhoto: PropTypes.bool,
  user: PropTypes.object.isRequired,
  save: PropTypes.func,
  saveImage: PropTypes.func,
  setMessage: PropTypes.func,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      required: PropTypes.bool,
    })
  ).isRequired,
  allowDisable: PropTypes.bool,
};
